/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";

import {
  ArrayInput,
  AutocompleteInput,
  FormDataConsumer,
  minValue,
  number,
  NumberInput,
  required,
  SelectInput,
  SimpleFormIterator,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { Grid, InputAdornment, Typography } from "@material-ui/core";

import { SmartTipIcon } from "../../../../components/common/SmartTipIcon";

import { CarrierType, CarrierTypes, Unit } from "../../../../utils/constants";
import { logError } from "../../../../utils/appUtils";
import {
  BillingAutomation,
  BillingAutomationChoices,
  ChargeEvent,
  ChargeEventChoices,
  FeeAccrual,
  FeeAccrualChoices,
  FreeDaysPolicy,
  FreeDaysPolicyChoices,
  StorageFrequency,
  StorageFrequencyChoices,
} from "./chargeRuleConstants";
import { filterAreas, filterZones } from "../../../../utils/locationUtils";
import {
  getRailEquipmentTypes,
  getTruckEquipmentTypes,
  getYardCommodity,
} from "../../../../utils/loadTypeUtils";
import { getUnitChoices } from "../../invoices/InvoiceUtils";
import { getUnitPrice } from "../../services/ServiceUtils";
import {
  fetchSetupData,
  getPeriodicStorageFrequencies,
  isEventDrivenStorageFrequency,
  isPeriodicStorageFrequency,
  ON_PULL_REQUIRES_RAIL,
} from "./ChargeRuleUtils";

const validateRequired = required();
const validateNumber = [number(), minValue(0)];
const validateRequiredNumber = [required(), number(), minValue(0)];
const wholeNumber = (value) =>
  value === "" || value == null || Number.isInteger(Number(value))
    ? undefined
    : "Must be a whole number";
const validateWholeNumber = [number(), minValue(0), wholeNumber];

const getGraduatedRateIndex = (source) => {
  const match = source?.match(/\[(\d+)\]\.[^.]+$/);
  return match ? Number(match[1]) : -1;
};
const validateGraduatedDays = (source, rateTiers) => (value) => {
  const rowIndex = getGraduatedRateIndex(source);
  const rateCount = Array.isArray(rateTiers) ? rateTiers.length : 0;
  const isOptionalLastRow = rateCount > 1 && rowIndex === rateCount - 1;

  if (value === "" || value == null) {
    return isOptionalLastRow ? undefined : required()(value);
  }

  return number()(value) || minValue(1, "Must be greater than 0")(value);
};

// A zone's lease binds only the account it is leased to
const getLeasedZoneBillingUOM = (zone, accountId) => {
  if (!zone?.accountId || zone.accountId !== accountId) return "";
  return zone.leasedAreaUOM || "";
};

const getStorageDefaults = (allRules, values) => {
  const ruleId = values.id;
  const serviceId = values.serviceId;
  const areaId = values.areaId;
  const zoneId = values.zoneId;
  const selectedFrequency = values.frequency;

  const blankRule = {
    frequency: "",
    feeAccrual: "",
    freeDaysPolicy: "",
    freeDays: null,
  };

  if (!allRules?.length) return blankRule;

  const storageRules = allRules.filter(
    (r) =>
      r.event === ChargeEvent.STORAGE &&
      (!ruleId || r.id !== ruleId) && // exclude self when editing
      (!serviceId || r.serviceId === serviceId), // service scoping for lowest-scope rules
  );
  if (!storageRules.length) return blankRule;

  // Once a storage frequency is chosen, only inherit defaults from rules with that same frequency.
  const filteredStorageRules = selectedFrequency
    ? storageRules.filter((r) => r.frequency === selectedFrequency)
    : storageRules;
  if (selectedFrequency && !filteredStorageRules.length) return blankRule;

  const periodicFrequencies =
    getPeriodicStorageFrequencies(filteredStorageRules);
  if (periodicFrequencies.length > 1) return blankRule;

  // Prefer rules scoped to this area, else fall back to any service-level storage rules
  const areaScoped = areaId
    ? filteredStorageRules.filter((r) => r.areaId === areaId)
    : [];
  const zoneScoped = zoneId
    ? areaScoped.filter((r) => r.zoneId === zoneId)
    : [];
  const pool = zoneScoped?.length
    ? zoneScoped
    : areaScoped?.length
      ? areaScoped
      : [];

  const frequencies = [
    ...new Set(pool.map((rule) => rule?.frequency).filter(Boolean)),
  ];
  if (!selectedFrequency && frequencies.length !== 1) return blankRule;

  // const base = (pool && pool[0]) || filteredStorageRules[0]; // use this to force defaults per service
  const base = pool[0]; // use this to force defaults per area/zone
  // All three defaults now come from the same rule, avoiding mixed/contradictory defaults.
  return {
    frequency: selectedFrequency || base?.frequency || "",
    feeAccrual: base?.feeAccrual ?? "",
    freeDaysPolicy: base?.freeDaysPolicy ?? "",
    freeDays: base?.freeDays ?? null,
  };
};

// Identifies the peer-rule pool that free-day defaults are prefilled from.
// While this key is unchanged the user's own free-day edits must stick, so a
// load-type-specific rule can carry its own free days (the create form's
// values subscription fires on every keystroke and would otherwise write the
// peer's values back over the override).
const getFreeDaysScopeKey = (values) =>
  [values.serviceId, values.areaId, values.zoneId, values.frequency]
    .map((v) => v || "")
    .join("|");

const getInitYardCommodity = (yardCommodities, record) => {
  const initYardCommodity = yardCommodities.find(
    (c) => c.id === record.reloadYardCommodityId,
  );
  return initYardCommodity;
};

export const ChargeRuleForm = ({
  record,
  account,
  allChargeRules,
  ...props
}) => {
  const yardCommodities = window.appUser.reloadYard.activeYardCommodities.items;
  const form = useForm();
  const notify = useNotify();
  const [areas, setAreas] = useState(
    window.appUser.reloadYard?.areas.items.filter(
      (itm) => itm.active === "true",
    ),
  );
  const [area, setArea] = useState();
  const [services, setServices] = useState(account.services?.items || []);
  const [loading, setLoading] = useState(true);
  const [frequency, setFrequency] = useState("");
  const [feeAccrual, setAccrual] = useState("");
  // Scope key of the last applied free-day prefill; null = not applied yet
  const freeDaysScopeRef = useRef(null);
  const prevEventRef = useRef(record?.event);
  const [yardCommodity, setYardCommodity] = useState(
    getInitYardCommodity(yardCommodities, record),
  );
  const [railEquipmentChoices, setRailEquipmentChoices] = useState(
    getRailEquipmentTypes(),
  );
  const [truckEquipmentChoices, setTruckEquipmentChoices] = useState(
    getTruckEquipmentTypes(),
  );
  const [carrierEquipmentChoices, setCarrierEquipmentChoices] = useState(
    record?.carrierType === CarrierType.RAIL
      ? railEquipmentChoices
      : record?.carrierType === CarrierType.TRUCK
        ? truckEquipmentChoices
        : [],
  );

  useEffect(() => {
    const getSetupData = async () => {
      try {
        setLoading(true);
        await fetchSetupData(
          account,
          setAreas,
          setServices,
          setRailEquipmentChoices,
          setTruckEquipmentChoices,
        );
      } catch (error) {
        logError("chargeRules:ChargeRuleForm:getSetupData", error, notify);
      } finally {
        setLoading(false);
      }
    };
    if (account) getSetupData();
  }, [account]);

  useEffect(() => {
    const sub = form.subscribe(
      ({ values }) => {
        const isStorage = values.event === ChargeEvent.STORAGE;
        const eventChanged = prevEventRef.current !== values.event;
        prevEventRef.current = values.event;
        if (!isStorage) {
          // Reset storage fields only on the switch away from STORAGE; the
          // resets notify this subscriber again, so resetting on every
          // values change recurses until the stack overflows.
          if (eventChanged)
            handleEventChange({ target: { value: values.event } });
          return;
        }

        if (!isEventDrivenStorageFrequency(values.frequency)) {
          const currentExecution = form.getFieldState("execution")?.value;
          if (currentExecution !== BillingAutomation.MANUAL) {
            form.change("execution", BillingAutomation.MANUAL);
          }
        }

        // Apply defaults when:
        // 1. User switches to STORAGE event for the first time
        // 2. User changes serviceId, areaId or zoneId (which should trigger new defaults)
        const shouldApplyDefaults =
          !record.id || // apply to all storage rules on creation
          record.areaId !== values.areaId ||
          record.zoneId !== values.zoneId ||
          record.serviceId !== values.serviceId; // if any of these fields change, apply defaults

        if (!shouldApplyDefaults) return;

        const { frequency, feeAccrual, freeDaysPolicy, freeDays } =
          getStorageDefaults(allChargeRules, values);

        // Frequency and accrual are locked to match other storage rules in
        // this location, so reapplying them on every change is safe. Free
        // days are prefilled from the broader rule only when the scope they
        // come from changes (see getFreeDaysScopeKey); the ref is only
        // stamped when defaults exist so peer rules that finish loading
        // after the scope was picked still prefill once.
        if (frequency) form.change("frequency", frequency);
        if (feeAccrual) form.change("feeAccrual", feeAccrual);

        const scopeKey = getFreeDaysScopeKey(values);
        if (
          freeDaysScopeRef.current !== scopeKey &&
          (freeDays !== null || freeDaysPolicy)
        ) {
          freeDaysScopeRef.current = scopeKey;
          if (freeDays !== null) form.change("freeDays", freeDays);
          if (freeDaysPolicy) form.change("freeDaysPolicy", freeDaysPolicy);
        }

        // Update local state for disabled logic
        setFrequency(frequency);
        setAccrual(feeAccrual);
      },
      { values: true }, // mask to subscribe to values changes
    );

    return sub.unsubscribe;
  }, [allChargeRules]);

  useEffect(() => {
    if (areas.length === 1) {
      form.change("areaId", areas[0].id);
      handleAreaChange(areas[0].id);
    } else if (record?.id && record.areaId) {
      const newArea = getNewArea(record.areaId);
      setArea(newArea);
    }
  }, [areas]);

  useEffect(() => {
    const sub = form.subscribe(
      ({ values }) => {
        const selectedZone = area?.zones?.items?.find(
          (zone) => zone.id === values.zoneId,
        );
        const leasedZoneBillingUOM = getLeasedZoneBillingUOM(
          selectedZone,
          account?.id,
        );
        if (!leasedZoneBillingUOM) return;

        if (values.carrierType) {
          form.change("carrierType", "");
        }

        if (values.freeCars != null) {
          form.change("freeCars", null);
        }

        if (values.carrierEquipmentTypeId) {
          form.change("carrierEquipmentTypeId", "");
        }

        if (carrierEquipmentChoices?.length) {
          setCarrierEquipmentChoices([]);
        }

        if (values.reloadYardCommodityId) {
          const allAreas = window.appUser.reloadYard?.areas.items || [];
          const selectedArea = allAreas.find(
            (areaRecord) => areaRecord.id === values.areaId,
          );

          form.change("reloadYardCommodityId", "");
          form.change("reloadYardCommodity", null);
          setYardCommodity(undefined);
          setAreas(allAreas);
          setArea(selectedArea);
        }

        if (values.billingUOM !== leasedZoneBillingUOM) {
          form.change("billingUOM", leasedZoneBillingUOM);
        }

        if (isEventDrivenStorageFrequency(values.frequency)) {
          form.change("frequency", "");
        }

        if (values.feeAccrual !== FeeAccrual.FLAT_RATE) {
          form.change("feeAccrual", FeeAccrual.FLAT_RATE);
          setAccrual(FeeAccrual.FLAT_RATE);
        }

        if (values.graduatedRates?.length) {
          form.change("graduatedRates", []);
        }

        if (values.freeDaysPolicy !== FreeDaysPolicy.DAYS) {
          form.change("freeDaysPolicy", FreeDaysPolicy.DAYS);
        }

        if (values.freeDays !== 0) {
          form.change("freeDays", 0);
        }
      },
      { values: true },
    );

    return sub.unsubscribe;
  }, [area, carrierEquipmentChoices, form, account?.id]);

  const handleCommodityChange = (valueOrEvent) => {
    const value = valueOrEvent?.target?.value ?? valueOrEvent;
    const yardComm = getYardCommodity(value);
    setYardCommodity && setYardCommodity(yardComm);
    form.change("reloadYardCommodity", yardComm);
    // Clear billingUOM if now invalid for this commodity
    const currentUom = form.getFieldState("billingUOM")?.value;
    const validUomList = yardComm?.commodity?.validUom || [];
    // If commodity selection is cleared (no yardComm), do not clear billingUOM
    if (
      yardComm?.id &&
      currentUom &&
      validUomList.length > 0 &&
      !validUomList.includes(currentUom)
    ) {
      // form.change("billingUOM", yardComm.units);
      form.change("billingUOM", "");
    }

    const filteredAreas = filterAreas(areas, yardComm);
    setAreas(filteredAreas);

    // Reset area selection if no longer valid for this commodity
    const currentAreaId = area?.id;
    if (currentAreaId && !filteredAreas.some((a) => a?.id === currentAreaId)) {
      form.change("areaId", "");
      form.change("zoneId", "");
    }
  };

  const getNewArea = (areaId) => {
    const newArea = { ...areas.find((itm) => itm.id === areaId) };
    newArea.zones = { ...newArea.zones };

    if (yardCommodity?.id && newArea.zones.items?.length) {
      newArea.zones.items = filterZones(
        newArea.zones.items,
        yardCommodity.id,
        account.id,
      );
    }
    return newArea;
  };

  const handleAreaChange = (areaId) => {
    const newArea = getNewArea(areaId);
    setArea(newArea);
    form.change("zoneId", "");
  };

  const handleServiceChange = () => {
    form.change("billingUOM", "");
  };

  const handleCarrierTypeChange = (event) => {
    const carrier = event?.target?.value;
    const currentUom = form.getFieldState("billingUOM")?.value;
    // If carrier selection conflicts with currently selected UOM, clear billingUOM
    const isShipmentUom = currentUom === Unit.CAR || currentUom === Unit.TL;
    if (!carrier && isShipmentUom) {
      form.change("billingUOM", "");
    } else if (carrier === CarrierType.RAIL && currentUom === Unit.TL) {
      form.change("billingUOM", "");
    } else if (carrier === CarrierType.TRUCK && currentUom === Unit.CAR) {
      form.change("billingUOM", "");
    }
    if (carrier === CarrierType.RAIL) {
      setCarrierEquipmentChoices(railEquipmentChoices);
    } else if (carrier === CarrierType.TRUCK) {
      setCarrierEquipmentChoices(truckEquipmentChoices);
    } else {
      setCarrierEquipmentChoices([]);
    }
    form.change("carrierEquipmentTypeId", "");
    // Free railcars and On Pull belong to rail rules alone
    if (carrier !== CarrierType.RAIL) {
      form.change("freeCars", null);
      if (form.getFieldState("frequency")?.value === StorageFrequency.ON_PULL)
        form.change("frequency", "");
    }
  };

  const handleFreeDaysPolicyChange = (event) => {
    const policy = event?.target?.value;
    if (policy === FreeDaysPolicy.END_OF_MONTH) {
      form.change("freeDays", 0);
    }
  };

  const handleEventChange = (event) => {
    const newEvent = event?.target?.value;

    if (newEvent !== ChargeEvent.STORAGE) {
      form.change("frequency", "");
      form.change("feeAccrual", "");
      form.change("freeDaysPolicy", "");
      form.change("freeDays", null);
      form.change("freeCars", null);
      setFrequency("");
      setAccrual("");
      freeDaysScopeRef.current = null;
    } else {
      // When switching to STORAGE, ensure execution is not MANUAL
      const currentExecution = form.getFieldState("execution")?.value;
      if (!currentExecution || currentExecution !== BillingAutomation.MANUAL) {
        form.change("execution", BillingAutomation.MANUAL);
      }
    }
    // The subscription useEffect handles STORAGE event cascading logic
  };

  const handleFrequencyChange = (event) => {
    const newFrequency = event?.target?.value;
    if (!isEventDrivenStorageFrequency(newFrequency)) {
      form.change("execution", BillingAutomation.MANUAL);
    } else {
      // Release-, pull- and depletion-driven storage bills each car on its
      // own, so there is no queue of cars for a free-railcar allowance to
      // apply to.
      form.change("freeCars", null);
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => {
        const isStorage = formData.event === ChargeEvent.STORAGE;
        const isGraduatedPricingEnabled =
          formData.feeAccrual === FeeAccrual.PRORATED_DAILY;
        // Free railcars: a rail rule, billed on a schedule (event-driven
        // storage bills one car at a time), and not a leased track.
        const canSetFreeCars =
          isStorage &&
          formData.carrierType === CarrierType.RAIL &&
          !isEventDrivenStorageFrequency(formData.frequency);
        const otherStorageRules = (allChargeRules || []).filter(
          (rule) =>
            rule?.event === ChargeEvent.STORAGE &&
            (!record?.id || rule.id !== record.id),
        );
        const periodicStorageFrequencies =
          getPeriodicStorageFrequencies(otherStorageRules);
        const canonicalPeriodicStorageFrequency =
          periodicStorageFrequencies.length === 1
            ? periodicStorageFrequencies[0]
            : "";
        const hasPeriodicStorageFrequencyConflict =
          periodicStorageFrequencies.length > 1;
        const executionChoices =
          isStorage && !isEventDrivenStorageFrequency(formData.frequency)
            ? BillingAutomationChoices.filter(
                (choice) => choice.id !== BillingAutomation.AUTO,
              )
            : BillingAutomationChoices;
        const selectedZone = area?.zones?.items?.find(
          (zone) => zone.id === formData?.zoneId,
        );
        const leasedZoneBillingUOM = getLeasedZoneBillingUOM(
          selectedZone,
          account?.id,
        );
        const isLeasedZone = Boolean(leasedZoneBillingUOM);
        const feeAccrualChoices = isLeasedZone
          ? FeeAccrualChoices.filter(
              (choice) => choice.id === FeeAccrual.FLAT_RATE,
            )
          : FeeAccrualChoices;
        const storageFrequencyChoices = StorageFrequencyChoices.filter(
          (choice) =>
            !isLeasedZone || !isEventDrivenStorageFrequency(choice.id),
        ).map((choice) => {
          // Only rail records a pull, so On Pull waits for Carrier Type Rail.
          if (choice.id === StorageFrequency.ON_PULL)
            return {
              ...choice,
              not_available: formData.carrierType !== CarrierType.RAIL,
            };
          if (!isPeriodicStorageFrequency(choice.id)) return choice;

          const not_available = hasPeriodicStorageFrequencyConflict
            ? choice.id !== formData.frequency
            : Boolean(
                canonicalPeriodicStorageFrequency &&
                choice.id !== canonicalPeriodicStorageFrequency,
              );

          return { ...choice, not_available };
        });
        const validateStorageFrequency = (value) => {
          const requiredError = validateRequired(value);
          if (requiredError) return requiredError;
          if (
            value === StorageFrequency.ON_PULL &&
            formData.carrierType !== CarrierType.RAIL
          )
            return ON_PULL_REQUIRES_RAIL;
          if (!isPeriodicStorageFrequency(value)) return undefined;
          if (hasPeriodicStorageFrequencyConflict) {
            return "Resolve conflicting periodic storage frequencies on this account before saving another periodic storage rule.";
          }
          if (
            canonicalPeriodicStorageFrequency &&
            value !== canonicalPeriodicStorageFrequency
          ) {
            return `Periodic storage frequency must remain ${canonicalPeriodicStorageFrequency} for this account.`;
          }
          return undefined;
        };
        // Build Billing UOM choices with commodity filtering and carrier gating
        // 1) Start from priced units available for selected service (fallback to record.service until services load)
        const serviceList = services?.length
          ? services
          : record?.service
            ? [record.service]
            : [];
        const selectedService =
          serviceList.find((service) => service.id === formData?.serviceId) ||
          (record?.service?.id === formData?.serviceId ? record.service : null);
        const firstTierServiceRate = getUnitPrice(
          selectedService,
          formData?.billingUOM,
          null,
        );
        const firstTierRatePlaceholder =
          firstTierServiceRate == null ? "" : firstTierServiceRate.toFixed(2);
        let unitChoices = getUnitChoices(formData, serviceList);

        // 2) Filter by selected Load Type's valid UOMs (from yardCommodity state)
        const validUom = yardCommodity?.commodity?.validUom;
        if (validUom?.length) {
          unitChoices = unitChoices.filter((c) => validUom.includes(c.id));
        }

        if (leasedZoneBillingUOM) {
          unitChoices = unitChoices.filter(
            (choice) => choice.id === leasedZoneBillingUOM,
          );
        }

        // 3) Add not_available flags like OutboundCreate for CAR/TL vs carrierType
        const carrier = formData?.carrierType;
        unitChoices = unitChoices.map((choice) => {
          const uom = choice?.id;
          let not_available = false;

          if (!carrier && (uom === Unit.CAR || uom === Unit.TL))
            not_available = true;
          else if (carrier === CarrierType.RAIL && uom === Unit.TL)
            not_available = true;
          else if (carrier === CarrierType.TRUCK && uom === Unit.CAR)
            not_available = true;

          // override for STORAGE event
          // only allow shipment level uoms for Truck & Rail storage
          if (isStorage) {
            if (carrier === CarrierType.RAIL && uom !== Unit.CAR) {
              not_available = true;
            } else if (carrier === CarrierType.TRUCK && uom !== Unit.TL) {
              not_available = true;
            }
          }

          return { ...choice, not_available };
        });

        // 4) If current selection became invalid for selected Load Type or carrier, clear it
        if (
          formData?.billingUOM &&
          unitChoices.length > 0 &&
          !unitChoices.some(
            (c) => c.id === formData.billingUOM && !c.not_available,
          )
        ) {
          form.change("billingUOM", "");
        }

        return (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    1. WHAT
                  </Typography>
                </legend>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="event"
                      label="Event"
                      choices={ChargeEventChoices}
                      onChange={handleEventChange}
                      validate={validateRequired}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <AutocompleteInput
                      source="serviceId"
                      label="Service"
                      choices={serviceList}
                      onChange={handleServiceChange}
                      disabled={loading}
                      validate={validateRequired}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="billingUOM"
                      label="Billing UOM"
                      choices={unitChoices}
                      disableValue="not_available"
                      validate={validateRequired}
                      disabled={!formData.serviceId || isLeasedZone}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                </Grid>
              </fieldset>
            </Grid>

            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    2.WHEN
                  </Typography>
                </legend>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="carrierType"
                      label="Carrier Type"
                      choices={CarrierTypes}
                      allowEmpty
                      onChange={handleCarrierTypeChange}
                      disabled={isLeasedZone}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="carrierEquipmentTypeId"
                      label="Carrier Equipment Type"
                      choices={carrierEquipmentChoices}
                      allowEmpty
                      fullWidth
                      disabled={
                        isLeasedZone || !carrierEquipmentChoices?.length
                      }
                      variant="outlined"
                      helperText={false}
                    />
                    <SmartTipIcon label="equipment-type-icon" mt={-1} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <AutocompleteInput
                      source="reloadYardCommodityId"
                      label="Load Type"
                      choices={yardCommodities}
                      optionText="commodity.name"
                      optionValue="id"
                      onChange={(value) => handleCommodityChange(value)}
                      allowEmpty
                      disabled={isLeasedZone}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                </Grid>
              </fieldset>
            </Grid>

            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    3. WHERE
                  </Typography>
                </legend>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="areaId"
                      label="Area"
                      choices={areas}
                      optionText="name"
                      optionValue="id"
                      disabled={loading}
                      onChange={(event) => handleAreaChange(event.target.value)}
                      allowEmpty
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="zoneId"
                      label="Zone"
                      choices={area?.zones?.items}
                      optionText="name"
                      optionValue="id"
                      disabled={!area || loading}
                      allowEmpty
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                </Grid>
              </fieldset>
            </Grid>

            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    4. HOW
                  </Typography>
                </legend>
                <Grid container spacing={2}>
                  {/* Storage: billing cycle, with graduated tiers beside the
                      accrual they belong to */}
                  {isStorage && (
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <SelectInput
                          source="frequency"
                          label="Frequency"
                          choices={storageFrequencyChoices}
                          disableValue="not_available"
                          onChange={handleFrequencyChange}
                          validate={validateStorageFrequency}
                          disabled={frequency != null && frequency !== ""}
                          fullWidth
                          variant="outlined"
                          helperText={false}
                        />
                        <SmartTipIcon label="frequency-icon" mt={-1} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <SelectInput
                          source="feeAccrual"
                          label="Fee Accrual"
                          choices={feeAccrualChoices}
                          validate={validateRequired}
                          disabled={
                            isLeasedZone ||
                            (feeAccrual != null && feeAccrual !== "")
                          }
                          fullWidth
                          variant="outlined"
                          helperText={false}
                        />
                        <SmartTipIcon label="fee-accrual-icon" mt={-1} />
                      </Grid>
                      {isGraduatedPricingEnabled && (
                        <Grid item xs={12} md={6}>
                          <ArrayInput
                            source="graduatedRates"
                            label={
                              <>
                                Graduated Daily Rates
                                <SmartTipIcon
                                  label="graduated-rates-icon"
                                  ml={3}
                                  mt={2}
                                  alignMiddle={true}
                                  alignRight={true}
                                />
                              </>
                            }
                          >
                            <SimpleFormIterator disableReordering>
                              <FormDataConsumer>
                                {({
                                  scopedFormData, // The data for this item of the ArrayInput
                                  getSource, // Function to get the valid source inside an ArrayInput. Translates to "inventories.items[].property"
                                  ...rest
                                }) => {
                                  const isFirstRateRow =
                                    getGraduatedRateIndex(getSource("rate")) ===
                                    0;

                                  return (
                                    <Grid container spacing={2}>
                                      <Grid item xs={6}>
                                        <NumberInput
                                          source={getSource("days")}
                                          label="Next N Days"
                                          validate={validateGraduatedDays(
                                            getSource("days"),
                                            formData?.graduatedRates,
                                          )}
                                          placeholder="All remaining days"
                                          InputLabelProps={{ shrink: true }}
                                          variant="outlined"
                                          fullWidth
                                          helperText={false}
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <NumberInput
                                          source={getSource("rate")}
                                          label="Daily Rate"
                                          validate={
                                            isFirstRateRow
                                              ? undefined
                                              : validateRequiredNumber
                                          }
                                          placeholder={
                                            isFirstRateRow
                                              ? firstTierRatePlaceholder
                                              : undefined
                                          }
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                $
                                              </InputAdornment>
                                            ),
                                            inputMode: "decimal",
                                          }}
                                          disabled={isFirstRateRow}
                                          variant="outlined"
                                          fullWidth
                                          helperText={false}
                                        />
                                      </Grid>
                                    </Grid>
                                  );
                                }}
                              </FormDataConsumer>
                            </SimpleFormIterator>
                          </ArrayInput>
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {/* Storage: what the customer gets before storage bills */}
                  {isStorage && (
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <SelectInput
                          source="freeDaysPolicy"
                          label="Free Days Policy"
                          choices={FreeDaysPolicyChoices}
                          defaultValue={FreeDaysPolicy.DAYS}
                          onChange={handleFreeDaysPolicyChange}
                          disabled={isLeasedZone}
                          fullWidth
                          variant="outlined"
                          helperText={false}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <NumberInput
                          source="freeDays"
                          label="Free Days"
                          validate={
                            formData?.freeDaysPolicy === FreeDaysPolicy.DAYS
                              ? validateRequiredNumber
                              : validateNumber
                          }
                          disabled={
                            isLeasedZone ||
                            formData?.freeDaysPolicy ===
                              FreeDaysPolicy.END_OF_MONTH
                          }
                          fullWidth
                          variant="outlined"
                          helperText={false}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <NumberInput
                          source="freeCars"
                          label="Free Railcars"
                          validate={validateWholeNumber}
                          disabled={!canSetFreeCars}
                          fullWidth
                          variant="outlined"
                          helperText={false}
                        />
                        <SmartTipIcon label="free-railcars" mt={-1} />
                      </Grid>
                    </Grid>
                  )}

                  {/* Charge limits and execution, every event */}
                  <Grid item xs={6} md={3}>
                    <NumberInput
                      source="minCharge"
                      label="Min $"
                      validate={validateNumber}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <NumberInput
                      source="maxCharge"
                      label="Max $"
                      validate={validateNumber}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <SelectInput
                      source="execution"
                      label="Execution"
                      choices={executionChoices}
                      allowEmpty
                      validate={validateRequired}
                      fullWidth
                      variant="outlined"
                      helperText={false}
                    />
                    <SmartTipIcon label="execution-icon" mt={-1} />
                  </Grid>
                </Grid>
              </fieldset>
            </Grid>
          </Grid>
        );
      }}
    </FormDataConsumer>
  );
};
